<template>
  <div class="thermal-comparison-dialog-root">
    <v-dialog
      v-model="dialogVisible"
      :max-width="modalLayoutSpecial.AI_THERMAL_COMPARE_MAX"
      scrollable
      v-bind="modalPolicy.P2_FORM"
      content-class="solvit-dialog-form-shell thermal-comparison-dialog-outer"
      @keydown.esc="dialogVisible = false"
    >
      <v-card
        class="thermal-comparison-dialog d-flex flex-column"
        :style="{
          maxHeight: modalMaxHeight,
          '--thermal-compare-max': modalLayoutSpecial.AI_THERMAL_COMPARE_MAX
        }"
      >
        <CrudFormDialogHeader
          :kicker="$t('map.toolbarCaptionAi')"
          :title="$t('map.aiDuplicateRemoval')"
          show-close
          @close="dialogVisible = false"
        />
        <v-progress-linear
          :active="loading"
          :indeterminate="loading"
          absolute
          color="primary"
        />

        <v-card-text class="pa-0 flex-grow-1 d-flex flex-column thermal-dialog-body">
          <v-alert
            type="info"
            dense
            outlined
            class="ma-3 mb-2"
          >
            {{ $t('map.aiDuplicateRemovalHintCheckbox') }}
          </v-alert>

          <div class="thermal-dialog-scroll flex-grow-1 px-3 pb-0">
            <div
              v-if="loading"
              class="text-center py-8"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="48"
              />
              <div class="text-body-2 text--secondary mt-4">
                {{ $t('table.loading') }}
              </div>
            </div>

            <div
              v-else-if="images.length === 0"
              class="py-4"
            >
              <v-alert type="info" outlined>
                {{ $t('map.aiDuplicateRemovalEmpty') }}
              </v-alert>
            </div>

            <div
              v-else
              class="thermal-comparison-nav-wrap"
            >
              <div class="thermal-pair-strip">
                <div class="thermal-pair-strip__images d-flex flex-grow-1 flex-nowrap">
                  <div
                    v-for="slot in currentImageSlots"
                    :key="slot.key"
                    class="thermal-pair-strip__cell d-flex flex-column align-center"
                  >
                    <div class="thermal-panel-nav d-flex align-center justify-center flex-wrap mb-1">
                      <span class="text-caption font-weight-medium thermal-panel-nav__label">
                        {{ panelLabel(slot.panelIndex) }}
                      </span>
                      <v-tooltip
                        bottom
                        :disabled="!dialogVisible"
                      >
                        <template slot="activator" slot-scope="{ on, attrs }">
                          <v-btn
                            icon
                            outlined
                            class="thermal-panel-nav__btn"
                            :disabled="slot.imageIndex <= 0"
                            :aria-label="panelNavPrevAriaLabel(slot.panelIndex)"
                            v-bind="attrs"
                            tabindex="-1"
                            v-on="on"
                            @click="movePanelPrev(slot.panelIndex)"
                          >
                            <v-icon large>
                              mdi-chevron-left
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>
                          {{ $t('defects.previousImage') }}
                          <span class="thermal-panel-nav-shortcut">
                            <span class="shortcut-key-badge">{{ panelNavPrevShortcutKey(slot.panelIndex) }}</span>
                          </span>
                        </span>
                      </v-tooltip>
                      <span class="text-body-2 text--secondary px-2 thermal-panel-nav__counter">
                        {{ imageCounterText(slot.imageIndex) }}
                      </span>
                      <v-tooltip
                        bottom
                        :disabled="!dialogVisible"
                      >
                        <template slot="activator" slot-scope="{ on, attrs }">
                          <v-btn
                            icon
                            outlined
                            class="thermal-panel-nav__btn"
                            :disabled="slot.imageIndex >= images.length - 1"
                            :aria-label="panelNavNextAriaLabel(slot.panelIndex)"
                            v-bind="attrs"
                            tabindex="-1"
                            v-on="on"
                            @click="movePanelNext(slot.panelIndex)"
                          >
                            <v-icon large>
                              mdi-chevron-right
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>
                          {{ $t('defects.nextImage') }}
                          <span class="thermal-panel-nav-shortcut">
                            <span class="shortcut-key-badge">{{ panelNavNextShortcutKey(slot.panelIndex) }}</span>
                          </span>
                        </span>
                      </v-tooltip>
                    </div>
                    <div class="thermal-image-frame">
                      <img
                        v-if="!slot.empty"
                        :src="slot.imageUrl"
                        class="thermal-image-frame__img"
                        :alt="slot.imageName"
                      >
                      <svg
                        class="polygon-overlay"
                        viewBox="0 0 640 512"
                        preserveAspectRatio="none"
                      >
                        <polygon
                          v-for="defect in slot.defects"
                          :key="defect.defectId"
                          :data-defect-id="defect.defectId"
                          :points="formatPoints(defect.coordinates)"
                          :class="{
                            selected: isSelected(defect.defectId),
                            'marked-for-delete': isMarkedForDelete(defect.defectId)
                          }"
                          class="defect-polygon"
                        />
                      </svg>
                      <div
                        class="thermal-marquee-layer"
                        @mousedown.prevent="onMarqueeStart($event, slot.panelIndex)"
                      />
                      <div
                        v-show="marquee.drawing && marquee.slotIndex === slot.panelIndex"
                        class="thermal-marquee-preview"
                        :style="getMarqueeStyle(slot.panelIndex)"
                      />
                    </div>
                    <div class="image-name mt-1 mb-0 text-center px-1">
                      {{ slot.imageName }}
                    </div>
                    <div
                      v-if="!slot.empty && slot.defects.length > 0"
                      class="thermal-defect-list mt-1"
                    >
                      <div
                        v-for="(defect, defectIndex) in slot.defects"
                        :key="defect.defectId"
                        class="thermal-defect-row d-flex align-center"
                        :class="{ 'thermal-defect-row--marked': isMarkedForDelete(defect.defectId) }"
                      >
                        <v-checkbox
                          :input-value="isMarkedForDelete(defect.defectId)"
                          hide-details
                          dense
                          class="thermal-defect-row__checkbox ma-0 pa-0"
                          :aria-label="$t('map.aiDuplicateRemovalMarkDeleteAria', {
                            n: defectIndex + 1
                          })"
                          @change="setMarkedForDelete(defect.defectId, $event)"
                        />
                        <button
                          type="button"
                          class="thermal-defect-row__label flex-grow-1 text-left"
                          @click="focusDefect(defect.defectId)"
                        >
                          {{ $t('map.aiDuplicateRemovalDefectItem', { n: defectIndex + 1 }) }}
                        </button>
                        <v-tooltip
                          bottom
                          :disabled="!dialogVisible"
                        >
                          <template slot="activator" slot-scope="{ on, attrs }">
                            <v-btn
                              icon
                              small
                              class="thermal-defect-row__magnify"
                              :aria-label="$t('map.aiDuplicateRemovalMagnifyAria', {
                                n: defectIndex + 1
                              })"
                              v-bind="attrs"
                              v-on="on"
                              @click="openMagnify(slot, defect)"
                            >
                              <v-icon small>
                                mdi-magnify
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('map.aiDuplicateRemovalMagnifyTooltip') }}</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-divider v-if="!loading && images.length > 0" />

        <v-card-actions
          v-if="!loading && images.length > 0"
          class="thermal-dialog-footer px-4 py-2"
        >
          <div class="thermal-dialog-footer__row">
            <div
              class="thermal-dialog-footer__hints"
              :aria-label="$t('map.aiDuplicateRemovalFooterHintsAria')"
            >
              <div class="thermal-footer-hint-row">
                <span class="thermal-footer-hint-chip">
                  <v-icon
                    x-small
                    class="thermal-footer-hint-chip__icon"
                  >
                    mdi-cursor-default-click
                  </v-icon>
                  {{ $t('map.aiDuplicateRemovalHintChipClick') }}
                </span>
                <span class="thermal-footer-hint-chip">
                  <v-icon
                    x-small
                    class="thermal-footer-hint-chip__icon"
                  >
                    mdi-select-drag
                  </v-icon>
                  {{ $t('map.aiDuplicateRemovalHintChipDrag') }}
                </span>
                <span class="thermal-footer-hint-chip">
                  <v-icon
                    x-small
                    class="thermal-footer-hint-chip__icon"
                  >
                    mdi-apple-keyboard-shift
                  </v-icon>
                  {{ $t('map.aiDuplicateRemovalHintChipShift') }}
                </span>
                <span class="thermal-footer-hint-chip">
                  <v-icon
                    x-small
                    class="thermal-footer-hint-chip__icon"
                  >
                    mdi-checkbox-marked-outline
                  </v-icon>
                  {{ $t('map.aiDuplicateRemovalHintChipCheckbox') }}
                </span>
                <span class="thermal-footer-hint-chip">
                  <v-icon
                    x-small
                    class="thermal-footer-hint-chip__icon"
                  >
                    mdi-magnify
                  </v-icon>
                  {{ $t('map.aiDuplicateRemovalHintChipMagnify') }}
                </span>
              </div>
              <div class="thermal-footer-hint-row thermal-footer-hint-row--keys">
                <span class="thermal-footer-hint-chip thermal-footer-hint-chip--label">
                  <v-icon
                    x-small
                    class="thermal-footer-hint-chip__icon"
                  >
                    mdi-keyboard-outline
                  </v-icon>
                  {{ $t('map.aiDuplicateRemovalHintShortcutsTitle') }}
                </span>
                <div class="thermal-footer-key-panel">
                  <span class="thermal-footer-key-panel__name">
                    {{ $t('map.aiDuplicateRemovalPanelLeft') }}
                  </span>
                  <span
                    class="thermal-footer-key-action"
                    :title="$t('map.aiDuplicateRemovalNavPrevLeft')"
                  >
                    <span class="shortcut-key-badge">1</span>
                    <v-icon x-small>mdi-chevron-left</v-icon>
                  </span>
                  <span
                    class="thermal-footer-key-action"
                    :title="$t('map.aiDuplicateRemovalNavNextLeft')"
                  >
                    <span class="shortcut-key-badge">2</span>
                    <v-icon x-small>mdi-chevron-right</v-icon>
                  </span>
                  <span
                    class="thermal-footer-key-action thermal-footer-key-action--danger"
                    :title="$t('map.aiDuplicateRemovalDeleteAllLeft')"
                  >
                    <span class="shortcut-key-badge">Q</span>
                    <v-icon x-small>mdi-delete-sweep</v-icon>
                  </span>
                </div>
                <span
                  class="thermal-footer-hint-sep"
                  aria-hidden="true"
                />
                <div class="thermal-footer-key-panel">
                  <span class="thermal-footer-key-panel__name">
                    {{ $t('map.aiDuplicateRemovalPanelRight') }}
                  </span>
                  <span
                    class="thermal-footer-key-action"
                    :title="$t('map.aiDuplicateRemovalNavPrevRight')"
                  >
                    <span class="shortcut-key-badge">3</span>
                    <v-icon x-small>mdi-chevron-left</v-icon>
                  </span>
                  <span
                    class="thermal-footer-key-action"
                    :title="$t('map.aiDuplicateRemovalNavNextRight')"
                  >
                    <span class="shortcut-key-badge">4</span>
                    <v-icon x-small>mdi-chevron-right</v-icon>
                  </span>
                  <span
                    class="thermal-footer-key-action thermal-footer-key-action--danger"
                    :title="$t('map.aiDuplicateRemovalDeleteAllRight')"
                  >
                    <span class="shortcut-key-badge">W</span>
                    <v-icon x-small>mdi-delete-sweep</v-icon>
                  </span>
                </div>
                <span
                  class="thermal-footer-hint-sep"
                  aria-hidden="true"
                />
                <span
                  class="thermal-footer-key-action thermal-footer-key-action--danger"
                  :title="$t('map.aiDuplicateRemovalDeleteMarkedAria')"
                >
                  <span class="shortcut-key-badge">E</span>
                  <v-icon x-small>mdi-delete-outline</v-icon>
                  <span class="thermal-footer-key-action__label">
                    {{ $t('map.aiDuplicateRemovalHintChipDeleteMarked') }}
                  </span>
                </span>
              </div>
            </div>
            <span
              v-if="markedForDeleteIds.length > 0"
              class="text-body-2 text--secondary thermal-dialog-footer__status"
            >
              {{ $t('map.aiDuplicateRemovalMarkedLabel', { n: markedForDeleteIds.length }) }}
            </span>
            <div class="thermal-dialog-footer__actions">
              <v-btn
                outlined
                class="solvit-crud-outline-btn"
                :disabled="markedForDeleteIds.length === 0"
                :aria-label="$t('map.aiDuplicateRemovalClearMarked')"
                @click="clearMarkedForDelete"
              >
                <v-icon left>
                  mdi-checkbox-blank-outline
                </v-icon>
                {{ $t('map.aiDuplicateRemovalClearMarked') }}
              </v-btn>
              <v-btn
                outlined
                class="solvit-crud-outline-btn solvit-crud-outline-btn--danger"
                :disabled="markedForDeleteIds.length === 0"
                :aria-label="$t('map.aiDuplicateRemovalDeleteMarkedAria')"
                @click="deleteMarkedDefects"
              >
                <v-icon left>
                  mdi-delete
                </v-icon>
                {{ $t('map.aiDuplicateRemovalDeleteMarked', { n: markedForDeleteIds.length }) }}
              </v-btn>
            </div>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="magnify.open"
      :max-width="modalLayout.MD"
      v-bind="modalPolicy.P1_DISMISSIBLE"
      content-class="thermal-magnify-dialog"
    >
      <v-card v-if="magnify.defect">
        <CrudFormDialogHeader
          :kicker="magnify.imageName"
          :title="$t('map.aiDuplicateRemovalMagnifyTitle')"
          show-close
          @close="closeMagnify"
        />
        <v-card-text class="pa-3">
          <div class="d-flex align-center flex-wrap mb-2">
            <v-switch
              v-model="magnify.showBbox"
              hide-details
              dense
              class="mt-0 pt-0"
              :label="$t('map.aiDuplicateRemovalMagnifyShowBbox')"
            />
          </div>
          <div class="thermal-magnify-frame">
            <svg
              class="thermal-magnify-frame__svg"
              :viewBox="magnifyViewBox"
              preserveAspectRatio="xMidYMid meet"
            >
              <image
                :href="magnify.imageUrl"
                x="0"
                y="0"
                width="640"
                height="512"
              />
              <polygon
                v-if="magnify.showBbox"
                :points="formatPoints(magnify.defect.coordinates)"
                class="thermal-magnify-bbox"
              />
            </svg>
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-3">
          <v-spacer />
          <v-btn
            text
            class="solvit-crud-text-btn"
            @click="closeMagnify"
          >
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import {
  MODAL_MAX_WIDTH,
  MODAL_CLOSE_POLICY,
  MODAL_LAYOUT_SPECIAL,
  MODAL_FORM_MAX_HEIGHT
} from '@/constants/modalLayout';
import { moduleDefectApi } from '../../apis/module.defect.api';

function ringCoordinatesFromGeom(geom) {
  if (!geom || !geom.type) return [];
  const c = geom.coordinates;
  if (geom.type === 'Polygon' && Array.isArray(c) && c[0]) {
    return c[0];
  }
  if (geom.type === 'MultiPolygon' && Array.isArray(c) && c[0] && c[0][0]) {
    return c[0][0];
  }
  return [];
}

const VIEW_W = 640;
const VIEW_H = 512;
const MARQUEE_MIN_DRAG = 6;

export default {
  name: 'ThermalImageComparisonDialog',
  components: {
    CrudFormDialogHeader
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    reportId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      modalLayout: MODAL_MAX_WIDTH,
      modalLayoutSpecial: MODAL_LAYOUT_SPECIAL,
      modalPolicy: MODAL_CLOSE_POLICY,
      images: [],
      leftIndex: 0,
      rightIndex: 1,
      selectedDefects: [],
      markedForDeleteIds: [],
      loading: false,
      magnify: {
        open: false,
        imageUrl: '',
        imageName: '',
        defect: null,
        showBbox: true
      },
      marquee: {
        drawing: false,
        slotIndex: null,
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 0
      },
      marqueeFrameRect: null
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    },
    currentImageSlots() {
      return [0, 1].map((panelIndex) => {
        const imageIndex = panelIndex === 0 ? this.leftIndex : this.rightIndex;
        const img = this.images[imageIndex];
        if (!img) {
          return {
            panelIndex,
            imageIndex,
            key: `empty-${panelIndex}-${imageIndex}`,
            empty: true,
            imageUrl: '',
            imageName: '',
            defects: []
          };
        }
        return {
          panelIndex,
          imageIndex,
          key: `${imageIndex}-${panelIndex}-${img.imageName}`,
          empty: false,
          imageUrl: img.imageUrl,
          imageName: img.imageName,
          defects: img.defects || []
        };
      });
    },
    modalMaxHeight() {
      return MODAL_FORM_MAX_HEIGHT;
    },
    keyboardShortcutsActive() {
      return (
        this.dialogVisible &&
        !this.loading &&
        this.images.length > 0 &&
        !this.magnify.open
      );
    },
    magnifyViewBox() {
      if (!this.magnify.defect) return `0 0 ${VIEW_W} ${VIEW_H}`;
      const b = this.polygonBounds(this.magnify.defect.coordinates);
      if (!b) return `0 0 ${VIEW_W} ${VIEW_H}`;
      const w = b.maxX - b.minX;
      const h = b.maxY - b.minY;
      const pad = Math.max(48, Math.max(w, h) * 0.75);
      const vbX = Math.max(0, b.minX - pad);
      const vbY = Math.max(0, b.minY - pad);
      const vbMaxX = Math.min(VIEW_W, b.maxX + pad);
      const vbMaxY = Math.min(VIEW_H, b.maxY + pad);
      return `${vbX} ${vbY} ${vbMaxX - vbX} ${vbMaxY - vbY}`;
    }
  },
  watch: {
    visible(val) {
      if (val && this.reportId) {
        this.fetchData();
      } else {
        this.resetState();
      }
    },
    keyboardShortcutsActive: {
      immediate: true,
      handler(active) {
        if (active) {
          this.attachKeyboard();
        } else {
          this.detachKeyboard();
        }
      }
    }
  },
  beforeDestroy() {
    this.detachMarqueeListeners();
    this.detachKeyboard();
  },
  methods: {
    ...mapActions('commonAlert', ['addAlert']),
    resetState() {
      this.images = [];
      this.leftIndex = 0;
      this.rightIndex = 1;
      this.selectedDefects = [];
      this.markedForDeleteIds = [];
      this.closeMagnify();
      this.resetMarquee();
    },
    attachKeyboard() {
      if (this._keyboardBound) return;
      document.addEventListener('keydown', this.onDocumentKeydown, true);
      this._keyboardBound = true;
    },
    detachKeyboard() {
      if (!this._keyboardBound) return;
      document.removeEventListener('keydown', this.onDocumentKeydown, true);
      this._keyboardBound = false;
    },
    isKeyboardInteractiveTarget(target) {
      if (!target || typeof target.closest !== 'function') return false;
      return !!target.closest(
        'button, [role="button"], a[href], input, textarea, select, [contenteditable="true"]'
      );
    },
    onDocumentKeydown(event) {
      if (!this.keyboardShortcutsActive) return;
      if (this.isKeyboardInteractiveTarget(event.target)) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.repeat) return;

      const handled = this.handleShortcutKey(event.code);
      if (handled) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    handleShortcutKey(code) {
      switch (code) {
        case 'Digit1':
        case 'Numpad1':
          if (this.leftIndex > 0) {
            this.movePanelPrev(0);
            return true;
          }
          return false;
        case 'Digit2':
        case 'Numpad2':
          if (this.leftIndex < this.images.length - 1) {
            this.movePanelNext(0);
            return true;
          }
          return false;
        case 'Digit3':
        case 'Numpad3':
          if (this.rightIndex > 0) {
            this.movePanelPrev(1);
            return true;
          }
          return false;
        case 'Digit4':
        case 'Numpad4':
          if (this.rightIndex < this.images.length - 1) {
            this.movePanelNext(1);
            return true;
          }
          return false;
        case 'KeyQ':
          this.deleteAllPanelDefects(0);
          return true;
        case 'KeyW':
          this.deleteAllPanelDefects(1);
          return true;
        case 'KeyE':
          if (this.markedForDeleteIds.length === 0) return false;
          this.deleteMarkedDefects();
          return true;
        default:
          return false;
      }
    },
    panelNavPrevShortcutKey(panelIndex) {
      return panelIndex === 0 ? '1' : '3';
    },
    panelNavNextShortcutKey(panelIndex) {
      return panelIndex === 0 ? '2' : '4';
    },
    panelNavPrevAriaLabel(panelIndex) {
      return `${this.$t('defects.previousImage')} (${this.panelNavPrevShortcutKey(panelIndex)})`;
    },
    panelNavNextAriaLabel(panelIndex) {
      return `${this.$t('defects.nextImage')} (${this.panelNavNextShortcutKey(panelIndex)})`;
    },
    getPanelDefectIds(panelIndex) {
      const img = this.images[this.panelImageIndex(panelIndex)];
      if (!img?.defects?.length) return [];
      return img.defects.map((d) => d.defectId);
    },
    initPanelIndices() {
      this.leftIndex = 0;
      this.rightIndex = this.images.length > 1 ? 1 : 0;
    },
    clampPanelIndex(index) {
      const len = this.images.length;
      if (len <= 0) return 0;
      return Math.max(0, Math.min(index, len - 1));
    },
    clampPanelIndices() {
      this.leftIndex = this.clampPanelIndex(this.leftIndex);
      this.rightIndex = this.clampPanelIndex(this.rightIndex);
    },
    panelImageIndex(panelIndex) {
      return panelIndex === 0 ? this.leftIndex : this.rightIndex;
    },
    panelLabel(panelIndex) {
      return panelIndex === 0
        ? this.$t('map.aiDuplicateRemovalPanelLeft')
        : this.$t('map.aiDuplicateRemovalPanelRight');
    },
    imageCounterText(imageIndex) {
      const total = Math.max(1, this.images.length);
      return this.$t('map.aiDuplicateRemovalPairCounter', {
        current: imageIndex + 1,
        total
      });
    },
    movePanelPrev(panelIndex) {
      const idx = this.panelImageIndex(panelIndex);
      if (idx <= 0) return;
      if (panelIndex === 0) {
        this.leftIndex -= 1;
      } else {
        this.rightIndex -= 1;
      }
      this.selectedDefects = [];
      this.resetMarquee();
    },
    movePanelNext(panelIndex) {
      const idx = this.panelImageIndex(panelIndex);
      if (idx >= this.images.length - 1) return;
      if (panelIndex === 0) {
        this.leftIndex += 1;
      } else {
        this.rightIndex += 1;
      }
      this.selectedDefects = [];
      this.resetMarquee();
    },
    resetMarquee() {
      this.detachMarqueeListeners();
      this.marquee = {
        drawing: false,
        slotIndex: null,
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 0
      };
      this.marqueeFrameRect = null;
      this._marqueeInteractionEl = null;
    },
    detachMarqueeListeners() {
      if (this._marqueeMove) {
        window.removeEventListener('mousemove', this._marqueeMove);
        this._marqueeMove = null;
      }
      if (this._marqueeUp) {
        window.removeEventListener('mouseup', this._marqueeUp);
        this._marqueeUp = null;
      }
    },
    clientToViewBox(clientX, clientY, frameRect) {
      const x = ((clientX - frameRect.left) / frameRect.width) * VIEW_W;
      const y = ((clientY - frameRect.top) / frameRect.height) * VIEW_H;
      return {
        x: Math.max(0, Math.min(VIEW_W, x)),
        y: Math.max(0, Math.min(VIEW_H, y))
      };
    },
    parseCoordPoints(coordinates) {
      if (typeof coordinates === 'string') {
        const parts = coordinates.trim().split(/\s+/).filter(Boolean);
        return parts.map((s) => {
          const [x, y] = s.split(',').map(Number);
          return [x, y];
        });
      }
      if (Array.isArray(coordinates)) {
        return coordinates.map((p) =>
          Array.isArray(p) ? [p[0], p[1]] : [p.x, p.y]
        );
      }
      return [];
    },
    polygonBounds(coords) {
      const pts = this.parseCoordPoints(coords);
      if (!pts.length) return null;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      pts.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });
      return { minX, minY, maxX, maxY };
    },
    normalizeRect(m) {
      const x1 = Math.min(m.x1, m.x2);
      const x2 = Math.max(m.x1, m.x2);
      const y1 = Math.min(m.y1, m.y2);
      const y2 = Math.max(m.y1, m.y2);
      return { minX: x1, minY: y1, maxX: x2, maxY: y2 };
    },
    rectsIntersect(a, b) {
      return !(
        a.maxX < b.minX ||
        a.minX > b.maxX ||
        a.maxY < b.minY ||
        a.minY > b.maxY
      );
    },
    onMarqueeStart(e, slotIndex) {
      const frame = e.currentTarget.closest('.thermal-image-frame');
      if (!frame) return;
      this._marqueeInteractionEl = e.currentTarget;
      this.marqueeFrameRect = frame.getBoundingClientRect();
      const vb = this.clientToViewBox(e.clientX, e.clientY, this.marqueeFrameRect);
      this.marquee.drawing = true;
      this.marquee.slotIndex = slotIndex;
      this.marquee.x1 = vb.x;
      this.marquee.y1 = vb.y;
      this.marquee.x2 = vb.x;
      this.marquee.y2 = vb.y;
      this._marqueeMove = (ev) => this.onMarqueeMove(ev);
      this._marqueeUp = (ev) => this.onMarqueeEnd(ev);
      window.addEventListener('mousemove', this._marqueeMove);
      window.addEventListener('mouseup', this._marqueeUp);
    },
    onMarqueeMove(e) {
      if (!this.marquee.drawing || !this.marqueeFrameRect) return;
      const vb = this.clientToViewBox(e.clientX, e.clientY, this.marqueeFrameRect);
      this.marquee.x2 = vb.x;
      this.marquee.y2 = vb.y;
    },
    onMarqueeEnd(e) {
      this.detachMarqueeListeners();
      if (!this.marquee.drawing || this.marquee.slotIndex === null) {
        this.resetMarquee();
        return;
      }
      const slotIdx = this.marquee.slotIndex;
      const norm = this.normalizeRect(this.marquee);
      const dx = Math.abs(this.marquee.x2 - this.marquee.x1);
      const dy = Math.abs(this.marquee.y2 - this.marquee.y1);
      this.marquee.drawing = false;
      this.marquee.slotIndex = null;
      this.marqueeFrameRect = null;

      if (dx < MARQUEE_MIN_DRAG && dy < MARQUEE_MIN_DRAG) {
        const layer = this._marqueeInteractionEl;
        this.resetMarquee();
        if (layer) {
          layer.style.pointerEvents = 'none';
          const hit = document.elementFromPoint(e.clientX, e.clientY);
          layer.style.pointerEvents = '';
          if (
            hit &&
            hit.tagName &&
            hit.tagName.toLowerCase() === 'polygon' &&
            hit.classList.contains('defect-polygon')
          ) {
            const raw = hit.getAttribute('data-defect-id');
            if (raw != null && raw !== '') {
              const n = Number(raw);
              const defectId = Number.isNaN(n) ? raw : n;
              this.toggleSelection(defectId, e);
            }
          } else if (!e.shiftKey) {
            this.clearSelection();
          }
        }
        return;
      }

      const shift = e.shiftKey;
      const img = this.images[this.panelImageIndex(slotIdx)];
      if (!img || !img.defects) {
        this.resetMarquee();
        return;
      }

      const picked = [];
      img.defects.forEach((d) => {
        const b = this.polygonBounds(d.coordinates);
        if (b && this.rectsIntersect(norm, b)) {
          picked.push(d.defectId);
        }
      });

      if (picked.length === 0) {
        this.resetMarquee();
        return;
      }

      if (shift) {
        const set = new Set(this.selectedDefects);
        picked.forEach((id) => set.add(id));
        this.selectedDefects = Array.from(set);
      } else {
        this.selectedDefects = picked;
      }
      this.resetMarquee();
    },
    getMarqueeStyle(slotIndex) {
      if (!this.marquee.drawing || this.marquee.slotIndex !== slotIndex) {
        return { display: 'none' };
      }
      const n = this.normalizeRect(this.marquee);
      const w = n.maxX - n.minX;
      const h = n.maxY - n.minY;
      if (w < 1 || h < 1) {
        return { display: 'none' };
      }
      return {
        left: `${(n.minX / VIEW_W) * 100}%`,
        top: `${(n.minY / VIEW_H) * 100}%`,
        width: `${(w / VIEW_W) * 100}%`,
        height: `${(h / VIEW_H) * 100}%`
      };
    },
    async fetchData() {
      if (!this.reportId) return;
      this.loading = true;
      try {
        const response = await moduleDefectApi.getAIDetectedDefects(this.reportId);
        const data = response.content || response;

        if (Array.isArray(data)) {
          this.images = data.map((item) => ({
            imageUrl: item.thermalImageUrl,
            imageName: item.thermalImageName,
            defects: (item.defects || []).map((d) => {
              const coords = ringCoordinatesFromGeom(d.defectShapeGeom);
              return {
                defectId: d.id,
                coordinates: coords
              };
            })
          }));
        } else {
          this.images = [];
        }
        this.initPanelIndices();
      } catch (error) {
        console.error('[ThermalImageComparisonDialog] fetchData', error);
        this.images = [];
        this.addAlert({
          type: 'error',
          message: this.$t('map.aiDuplicateRemovalLoadError'),
          timeout: 6000
        });
        this.initPanelIndices();
      } finally {
        this.loading = false;
      }
    },
    formatPoints(coordinates) {
      if (typeof coordinates === 'string') return coordinates;
      if (Array.isArray(coordinates)) {
        return coordinates
          .map((p) => {
            if (Array.isArray(p)) return `${p[0]},${p[1]}`;
            return `${p.x},${p.y}`;
          })
          .join(' ');
      }
      return '';
    },
    isSelected(defectId) {
      return this.selectedDefects.includes(defectId);
    },
    isMarkedForDelete(defectId) {
      return this.markedForDeleteIds.includes(defectId);
    },
    setMarkedForDelete(defectId, checked) {
      const index = this.markedForDeleteIds.indexOf(defectId);
      if (checked && index === -1) {
        this.markedForDeleteIds.push(defectId);
      } else if (!checked && index !== -1) {
        this.markedForDeleteIds.splice(index, 1);
      }
    },
    clearMarkedForDelete() {
      this.markedForDeleteIds = [];
    },
    focusDefect(defectId) {
      this.selectedDefects = [defectId];
    },
    openMagnify(slot, defect) {
      this.magnify = {
        open: true,
        imageUrl: slot.imageUrl,
        imageName: slot.imageName,
        defect,
        showBbox: true
      };
      this.focusDefect(defect.defectId);
    },
    closeMagnify() {
      this.magnify = {
        open: false,
        imageUrl: '',
        imageName: '',
        defect: null,
        showBbox: true
      };
    },
    clearSelection() {
      this.selectedDefects = [];
      this.resetMarquee();
    },
    toggleSelection(defectId, event) {
      if (event.shiftKey) {
        const index = this.selectedDefects.indexOf(defectId);
        if (index === -1) {
          this.selectedDefects.push(defectId);
        } else {
          this.selectedDefects.splice(index, 1);
        }
      } else if (
        this.selectedDefects.length === 1 &&
        this.selectedDefects[0] === defectId
      ) {
        this.selectedDefects = [];
      } else {
        this.selectedDefects = [defectId];
      }
    },
    async deleteMarkedDefects() {
      if (this.markedForDeleteIds.length === 0) return;
      const ids = [...this.markedForDeleteIds];
      await this.deleteDefectsByIds(ids);
    },
    async deleteAllPanelDefects(panelIndex) {
      const ids = this.getPanelDefectIds(panelIndex);
      if (ids.length === 0) return;
      await this.deleteDefectsByIds(ids);
    },
    async deleteDefectsByIds(ids) {
      if (!ids.length) return;
      try {
        await moduleDefectApi.deleteModuleDefects(ids);

        this.images.forEach((img) => {
          if (img.defects) {
            img.defects = img.defects.filter((d) => !ids.includes(d.defectId));
          }
        });
        this.images = this.images.filter(
          (img) => img.defects && img.defects.length > 0
        );

        this.selectedDefects = [];
        this.markedForDeleteIds = this.markedForDeleteIds.filter(
          (id) => !ids.includes(id)
        );
        this.clampPanelIndices();

        this.addAlert({
          type: 'success',
          message: this.$t('map.aiDuplicateRemovalDeleted'),
          timeout: 4000
        });
        this.$emit('defects-removed', { defectIds: ids });
      } catch (error) {
        console.error('[ThermalImageComparisonDialog] deleteDefectsByIds', error);
        this.addAlert({
          type: 'error',
          message: this.$t('map.aiDuplicateRemovalDeleteFailed'),
          timeout: 6000
        });
      }
    }
  }
};
</script>

<style scoped>
.image-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--v-text-base);
  word-break: break-all;
}

.thermal-dialog-scroll {
  overflow-y: auto;
  min-height: 0;
}

.thermal-comparison-nav-wrap {
  position: relative;
  min-height: 240px;
}

.thermal-pair-strip {
  min-width: 0;
}

.thermal-pair-strip__images {
  gap: 0;
  width: fit-content;
  max-width: 100%;
  margin-inline: auto;
  min-width: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
  background: var(--v-surface-base);
}

.theme--dark .thermal-pair-strip__images {
  border-color: rgba(255, 255, 255, 0.12);
}

/* 열당 최대 600px — 2열·패딩·테두리 합이 modalLayout AI_THERMAL_COMPARE_MAX 안에 들어가도록 */
.thermal-panel-nav {
  gap: 4px;
  width: 100%;
  min-height: 48px;
}

.thermal-panel-nav__btn.v-btn--icon {
  width: 48px;
  height: 48px;
  border-radius: 4px;
  background-color: var(--v-surface-base);
}

.thermal-panel-nav__btn.v-btn--outlined {
  border: 1px solid rgba(0, 0, 0, 0.28) !important;
}

.theme--dark .thermal-panel-nav__btn.v-btn--outlined {
  border-color: rgba(255, 255, 255, 0.32) !important;
  background-color: rgba(255, 255, 255, 0.04);
}

.thermal-panel-nav__btn.v-btn--icon:not(.v-btn--disabled):hover {
  border-color: rgba(0, 0, 0, 0.42) !important;
  background-color: rgba(0, 0, 0, 0.04);
}

.theme--dark .thermal-panel-nav__btn.v-btn--icon:not(.v-btn--disabled):hover {
  border-color: rgba(255, 255, 255, 0.48) !important;
  background-color: rgba(255, 255, 255, 0.08);
}

.thermal-panel-nav__btn.v-btn--disabled {
  opacity: 0.5;
}

.thermal-panel-nav__btn.v-btn--icon .v-icon {
  font-size: 32px;
}

.thermal-panel-nav__label {
  min-width: 2.5rem;
  text-align: center;
}

.thermal-panel-nav__counter {
  min-width: 7rem;
  text-align: center;
}

.thermal-pair-strip__cell {
  flex: 0 0 auto;
  width: min(600px, calc((100vw - 100px) / 2));
  min-width: 0;
  padding: 2px 2px 0;
  box-sizing: border-box;
}

.thermal-pair-strip__cell + .thermal-pair-strip__cell {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}

.theme--dark .thermal-pair-strip__cell + .thermal-pair-strip__cell {
  border-left-color: rgba(255, 255, 255, 0.12);
}

.thermal-dialog-footer {
  align-items: center;
  box-sizing: border-box;
}

.thermal-dialog-footer__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 8px 12px;
  min-height: 40px;
}

.thermal-dialog-footer__hints {
  flex: 1 1 12rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.thermal-footer-hint-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
}

.thermal-footer-hint-row--keys {
  gap: 8px 10px;
}

.thermal-footer-hint-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.05);
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--v-text-secondary);
  white-space: nowrap;
}

.theme--dark .thermal-footer-hint-chip {
  background: rgba(255, 255, 255, 0.08);
}

.thermal-footer-hint-chip--label {
  font-weight: 600;
  color: var(--v-text-secondary);
}

.thermal-footer-hint-chip__icon {
  opacity: 0.85;
}

.thermal-footer-key-panel {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
}

.thermal-footer-key-panel__name {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--v-text-secondary);
  margin-right: 2px;
}

.thermal-footer-key-action {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 4px;
  border-radius: 4px;
  cursor: default;
}

.thermal-footer-key-action__label {
  font-size: 0.75rem;
  color: var(--v-text-secondary);
  margin-left: 2px;
}

.thermal-footer-key-action--danger .thermal-footer-key-action__label {
  color: var(--v-error-base, #ff5252);
}

.thermal-footer-hint-sep {
  width: 1px;
  height: 1.25rem;
  background: rgba(0, 0, 0, 0.16);
  flex-shrink: 0;
}

.theme--dark .thermal-footer-hint-sep {
  background: rgba(255, 255, 255, 0.2);
}

.thermal-dialog-footer__status {
  flex: 0 0 auto;
  white-space: nowrap;
}

.thermal-dialog-footer__actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 8px;
  margin-left: auto;
}

.thermal-image-frame {
  position: relative;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 640 / 512;
  background: var(--v-surface-base);
}

.thermal-image-frame__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.polygon-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.polygon-overlay polygon {
  pointer-events: auto;
  vector-effect: non-scaling-stroke;
  paint-order: stroke fill;
  stroke-linejoin: round;
  stroke-linecap: round;
  transition: stroke-width var(--solvit-motion-duration) var(--solvit-motion-easing),
    fill var(--solvit-motion-duration) var(--solvit-motion-easing),
    stroke var(--solvit-motion-duration) var(--solvit-motion-easing),
    filter var(--solvit-motion-duration) var(--solvit-motion-easing);
}

/*
 * IR 열화상은 보통 회색·적색·황색 계열이 많아 빨강/주황 계열과 구분이 어렵다.
 * 미선택: 청록 + 스트로크 + 어두운 외곽 글로우 / 선택: 마젠타 + 더 굵은 스트로크
 * fill: transparent — 보이는 채움은 없지만 SVG 히트 영역은 면 전체(fill: none이면 내부 클릭 불가)
 */
.defect-polygon {
  fill: transparent;
  stroke: #00e5ff;
  stroke-width: 2px;
  filter: drop-shadow(0 0 0.5px #0d0d0d) drop-shadow(0 0 2px rgba(0, 0, 0, 0.9));
  cursor: pointer;
}

.polygon-overlay polygon.defect-polygon:hover:not(.selected) {
  stroke-width: 2.5px;
  stroke: #5cfffa;
  fill: transparent;
  filter: drop-shadow(0 0 0.5px #000) drop-shadow(0 0 2px rgba(0, 0, 0, 0.95))
    drop-shadow(0 0 4px rgba(0, 255, 250, 0.55));
}

.polygon-overlay polygon.defect-polygon.selected {
  stroke: #ff00e5 !important;
  stroke-width: 3px !important;
  fill: transparent !important;
  filter: drop-shadow(0 0 1px #fff) drop-shadow(0 0 2px #0a0a0a) drop-shadow(0 0 5px rgba(255, 0, 229, 0.85)) !important;
}

.polygon-overlay polygon.defect-polygon.selected:hover {
  stroke: #ff4df5 !important;
  stroke-width: 3px !important;
  fill: transparent !important;
}

.thermal-marquee-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  cursor: crosshair;
  background: transparent;
}

.thermal-marquee-preview {
  position: absolute;
  z-index: 3;
  pointer-events: none;
  box-sizing: border-box;
  border: 2px dashed var(--v-primary-base);
  background: rgba(33, 150, 243, 0.08);
}

.polygon-overlay polygon.defect-polygon.marked-for-delete {
  stroke: #ff5252 !important;
  stroke-width: 2.5px !important;
  stroke-dasharray: 6 4;
  fill: rgba(255, 82, 82, 0.08) !important;
  filter: drop-shadow(0 0 1px #0a0a0a) drop-shadow(0 0 3px rgba(255, 82, 82, 0.65)) !important;
}

.polygon-overlay polygon.defect-polygon.marked-for-delete.selected {
  stroke: #ff1744 !important;
  stroke-width: 3px !important;
}

.thermal-defect-list {
  width: 100%;
  max-height: 140px;
  overflow-y: auto;
  padding: 0 4px 4px;
  box-sizing: border-box;
}

.thermal-defect-row {
  gap: 2px;
  min-height: 32px;
  border-radius: 4px;
  padding: 0 2px;
}

.thermal-defect-row--marked {
  background: rgba(255, 82, 82, 0.08);
}

.thermal-defect-row__checkbox {
  flex: 0 0 auto;
}

.thermal-defect-row__checkbox ::v-deep .v-input--selection-controls__input {
  margin-right: 0;
}

.thermal-defect-row__label {
  border: 0;
  background: transparent;
  font-size: 0.8125rem;
  color: var(--v-text-primary);
  padding: 4px 6px;
  cursor: pointer;
}

.thermal-defect-row__label:hover {
  text-decoration: underline;
}

.thermal-defect-row__magnify {
  flex: 0 0 auto;
}

.thermal-magnify-frame {
  width: 100%;
  aspect-ratio: 4 / 3;
  max-height: min(560px, calc(100vh - 220px));
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background: #111;
  overflow: hidden;
}

.theme--dark .thermal-magnify-frame {
  border-color: rgba(255, 255, 255, 0.12);
}

.thermal-magnify-frame__svg {
  display: block;
  width: 100%;
  height: 100%;
}

.thermal-magnify-bbox {
  fill: transparent;
  stroke: #ff00e5;
  stroke-width: 2px;
  vector-effect: non-scaling-stroke;
  paint-order: stroke fill;
}

.thermal-dialog-body {
  background-color: var(--v-background-base);
  min-height: 0;
}

/* 카드 가로: 이미지 2열 + 헤더·액션에 맞춤 (--thermal-compare-max, 뷰포트보다 좁게) */
.thermal-comparison-dialog {
  width: fit-content;
  max-width: min(var(--thermal-compare-max, 600px), calc(100vw - 32px));
  min-width: 0;
  margin-inline: auto;
  box-sizing: border-box;
}

</style>

<style>
/* v-tooltip 본문은 body로 teleport — DialogEdgeNavigation 과 동일 배지 스타일 */
.thermal-panel-nav-shortcut {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
}

.thermal-panel-nav-shortcut .shortcut-key-badge,
.thermal-footer-key-action .shortcut-key-badge {
  background: color-mix(in srgb, var(--v-secondary-base) 22%, var(--v-surface-base));
  padding: 1px 6px;
  border-radius: 2px;
  margin: 0 1px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
}

.thermal-footer-key-action--danger .shortcut-key-badge {
  background: color-mix(in srgb, var(--v-error-base, #ff5252) 18%, var(--v-surface-base));
}
</style>
